import { useMemo, useEffect, useState } from "react"
import { Demographics, BehaviorSignals, Goal } from "../types"

// Activity factors mapping for calorie calculation
const ACTIVITY_FACTORS = {
  sedentary: 1.2,           // Little/no exercise
  light: 1.375,             // Light exercise 1-3 days/week
  moderate: 1.55,           // Moderate exercise 3-5 days/week
  heavy: 1.725              // Hard exercise 6-7 days/week
} as const

const DEFAULT_RATE_PER_WEEK = 0.5 as const // Default weight change rate in kg/week

// Base meal distribution percentages
const BASE_MEAL_PERCENTAGES = {
  'once_awake': 2.5,
  'breakfast': 30,
  'morning_snack': 10,
  'lunch': 25,
  'evening_snack': 10,
  'dinner': 20,
  'bed_time': 2.5
} as const

interface CalorieCalculationResult {
  bmr: number
  totalCalorieNeed: number
  adjustedCalories: number
  macros: {
    carbs: number
    protein: number
    fats: number
  }
  mealDistribution: {
    [key: string]: number
  }
}

interface UseCalorieCalculationProps {
  demographics: Demographics
  behaviorSignals: BehaviorSignals
  goal: Goal
  macroSplitPct: {
    carbs: number
    protein: number
    fats: number
  }
  mealTypes: {
    once_awake: boolean
    early_morning: boolean
    breakfast: boolean
    morning_snack: boolean
    lunch: boolean
    evening_snack: boolean
    dinner: boolean
    post_dinner: boolean
    bed_time: boolean
  }
  isFormInitialized: boolean
  isEditingTargets: boolean
}

export const useCalorieCalculation = ({
  demographics,
  behaviorSignals,
  goal,
  macroSplitPct,
  mealTypes,
  isFormInitialized,
  isEditingTargets
}: UseCalorieCalculationProps) => {
  const [calculatedValues, setCalculatedValues] = useState<CalorieCalculationResult | null>(null)

  // Calculate BMR using Mifflin-St Jeor Equation
  const calculateBMR = useMemo(() => {
    return () => {
      const { gender, age, height_cm, weight_kg } = demographics
      
      // Validate inputs
      if (!age || !height_cm || !weight_kg) {
        return 0
      }
      
      // BMR calculation based on gender
      // Males: BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age + 5
      // Females: BMR = 10 × weight(kg) + 6.25 × height(cm) - 5 × age - 161
      const base = 10 * weight_kg + 6.25 * height_cm - 5 * age
      return gender === "M" ? base + 5 : base - 161
    }
  }, [demographics])

  // Calculate total calorie need based on BMR and activity factor
  const calculateTotalCalorieNeed = useMemo(() => {
    return () => {
      const bmr = calculateBMR()
      if (bmr === 0) return 0
      
      const activityLevel = behaviorSignals?.activity_level
      if (!activityLevel) return 0
      
      // Formula:
      // Total Calorie Need = BMR × activity_factor
      const activityFactor = ACTIVITY_FACTORS[activityLevel as keyof typeof ACTIVITY_FACTORS] || 1.2
      return Math.round(bmr * activityFactor)
    }
  }, [calculateBMR, behaviorSignals?.activity_level])

  // Calculate macros based on calories and macro split percentages
  const calculateMacros = useMemo(() => {
    return (totalCalories: number) => {
      /* 
      * Macronutrient Conversions Formula:
      * - Carbs: calories ÷ 4 = grams
      * - Protein: calories ÷ 4 = grams  
      * - Fat: calories ÷ 9 = grams
      */
      const { carbs, protein, fats } = macroSplitPct
      return {
        carbs: Math.round((totalCalories * carbs) / 100 / 4),
        protein: Math.round((totalCalories * protein) / 100 / 4),
        fats: Math.round((totalCalories * fats) / 100 / 9),
      }
    }
  }, [macroSplitPct])

  // Calculate adjusted calories based on goal
  const calculateAdjustedCalories = useMemo(() => {
    return (totalCalorieNeed: number) => {
      let adjustedCalories = totalCalorieNeed
      
      /* 
      * Goal Calorie Adjustments Formula:
      * - Weight Loss: total_calorie_need - (rate_per_week × 1000)
      * - Weight Gain: total_calorie_need + (rate_per_week × 1000) 
      * - Maintenance: total_calorie_need
      */
      const ratePerWeek = goal?.rate_per_week ?? DEFAULT_RATE_PER_WEEK
      
      if (goal?.type === "loss") {
        adjustedCalories -= ratePerWeek * 1000 // kcal/day deficit based on rate
      } else if (goal?.type === "gain") {
        adjustedCalories += ratePerWeek * 1000 // kcal/day surplus based on rate
      }
      
      return Math.round(adjustedCalories)
    }
  }, [goal?.type, goal?.rate_per_week])

  // Calculate meal distribution based on selected meals and total calories
  const calculateMealDistribution = useMemo(() => {
    return (totalCalories: number) => {
      // Get selected meals
      const selectedMeals = Object.entries(mealTypes).filter(([_, isSelected]) => isSelected)
      
      if (selectedMeals.length === 0) {
        return {}
      }

      // Calculate total percentage of selected meals
      let totalSelectedPercentage = 0
      const selectedMealPercentages: { [key: string]: number } = {}
      
      selectedMeals.forEach(([mealKey, _]) => {
        const percentage = BASE_MEAL_PERCENTAGES[mealKey as keyof typeof BASE_MEAL_PERCENTAGES] || 0
        selectedMealPercentages[mealKey] = percentage
        totalSelectedPercentage += percentage
      })

      // If all meals are selected, use base percentages
      if (totalSelectedPercentage >= 100) {
        const distribution: { [key: string]: number } = {}
        selectedMeals.forEach(([mealKey, _]) => {
          const percentage = BASE_MEAL_PERCENTAGES[mealKey as keyof typeof BASE_MEAL_PERCENTAGES] || 0
          distribution[mealKey] = Math.round((totalCalories * percentage) / 100)
        })
        return distribution
      }

      // If not all meals are selected, distribute remaining percentage equally
      const remainingPercentage = 100 - totalSelectedPercentage
      const equalDistribution = remainingPercentage / selectedMeals.length
      
      const distribution: { [key: string]: number } = {}
      selectedMeals.forEach(([mealKey, _]) => {
        const basePercentage = BASE_MEAL_PERCENTAGES[mealKey as keyof typeof BASE_MEAL_PERCENTAGES] || 0
        const finalPercentage = basePercentage + equalDistribution
        distribution[mealKey] = Math.round((totalCalories * finalPercentage) / 100)
      })

      return distribution
    }
  }, [mealTypes])

  // Main calculation function
  const performCalculation = useMemo(() => {
    return () => {
      const { age, height_cm, weight_kg, gender } = demographics
      const activityLevel = behaviorSignals?.activity_level

      // Only calculate if we have all required values
      if (!age || !height_cm || !weight_kg || !gender || !activityLevel) {
        return null
      }

      const bmr = calculateBMR()
      if (bmr === 0) return null

      const totalCalorieNeed = calculateTotalCalorieNeed()
      if (totalCalorieNeed === 0) return null

      const adjustedCalories = calculateAdjustedCalories(totalCalorieNeed)
      const macros = calculateMacros(adjustedCalories)
      const mealDistribution = calculateMealDistribution(adjustedCalories)

      return {
        bmr,
        totalCalorieNeed,
        adjustedCalories,
        macros,
        mealDistribution
      }
    }
  }, [
    demographics,
    behaviorSignals?.activity_level,
    calculateBMR,
    calculateTotalCalorieNeed,
    calculateAdjustedCalories,
    calculateMacros,
    calculateMealDistribution
  ])

  // Auto-calculate when dependencies change
  useEffect(() => {
    if (!isFormInitialized || isEditingTargets) return

    const result = performCalculation()
    if (result) {
      setCalculatedValues(result)
    }
  }, [
    demographics,
    behaviorSignals?.activity_level,
    goal?.type,
    goal?.rate_per_week,
    macroSplitPct,
    isFormInitialized,
    isEditingTargets,
    performCalculation
  ])

  // Manual calculation trigger
  const recalculate = () => {
    const result = performCalculation()
    if (result) {
      setCalculatedValues(result)
    }
  }

  // Reset calculated values
  const resetCalculation = () => {
    setCalculatedValues(null)
  }

  return {
    calculatedValues,
    recalculate,
    resetCalculation,
    // Individual calculation functions for external use
    calculateBMR,
    calculateTotalCalorieNeed,
    calculateAdjustedCalories,
    calculateMacros,
    calculateMealDistribution,
    // Constants
    ACTIVITY_FACTORS,
    BASE_MEAL_PERCENTAGES
  }
}
